import { createContext, PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react';

import { addDayOff, getDaysOff, removeDayOff } from '../settings/api';
import { useSettings } from '../settings/SettingsProvider';
import { useCurrentUserState } from './currentUserState';
import type { Caller } from './models';

type CallerStatus = 'loading' | 'ready' | 'error';

export type CurrentUserContextValue = {
    status: CallerStatus;
    user: Caller | null;
    daysOff: Set<string>;
    daysOffVersion: number;
    canEditDaysOff: boolean;
    toggleDayOff: (date: string) => Promise<void>;
    refreshDaysOff: () => Promise<void>;
    error: string | null;
};

export const CurrentUserContext = createContext<CurrentUserContextValue | undefined>(undefined);

export const CurrentUserProvider: React.FC<PropsWithChildren> = ({ children }) => {
    const { configured, settings } = useSettings();
    const loaded = useCurrentUserState(configured, settings.org, settings.pat);

    const [daysOff, setDaysOffState] = useState<Set<string>>(new Set());
    const [daysOffVersion, setDaysOffVersion] = useState(0);
    const [localError, setLocalError] = useState<string | null>(null);

    useEffect(() => {
        setDaysOffState(loaded.daysOff);
        setLocalError(null);
    }, [loaded.daysOff]);

    const toggleDayOff = useCallback(
        async (date: string) => {
            if (!loaded.user) {
                return;
            }
            const isAdding = !daysOff.has(date);
            const next = new Set(daysOff);
            if (isAdding) {
                next.add(date);
            } else {
                next.delete(date);
            }
            // Optimistic — the writes are idempotent on the server side, so
            // a refresh re-syncs cleanly if anything is off.
            setDaysOffState(next);
            try {
                if (isAdding) {
                    await addDayOff(loaded.user.id, date);
                } else {
                    await removeDayOff(loaded.user.id, date);
                }
                setDaysOffVersion((v) => v + 1);
                setLocalError(null);
            } catch (e) {
                // Roll back the optimistic flip on failure.
                setDaysOffState(daysOff);
                setLocalError(e instanceof Error ? e.message : 'Failed to update days off.');
            }
        },
        [loaded.user, daysOff],
    );

    const refreshDaysOff = useCallback(async () => {
        if (!loaded.user) {
            return;
        }
        try {
            const { items } = await getDaysOff(loaded.user.id);
            setDaysOffState(new Set(items));
            setDaysOffVersion((v) => v + 1);
            setLocalError(null);
        } catch (e) {
            setLocalError(e instanceof Error ? e.message : 'Failed to refresh days off.');
        }
    }, [loaded.user]);

    const value = useMemo<CurrentUserContextValue>(
        () => ({
            status: loaded.status,
            user: loaded.user,
            daysOff,
            daysOffVersion,
            canEditDaysOff: loaded.user !== null,
            toggleDayOff,
            refreshDaysOff,
            error: localError ?? loaded.error,
        }),
        [loaded.status, loaded.user, loaded.error, daysOff, daysOffVersion, toggleDayOff, refreshDaysOff, localError],
    );

    return <CurrentUserContext.Provider value={value}>{children}</CurrentUserContext.Provider>;
};
